import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

export default function EditUserDialog({ target, roles, onClose, onSaved }) {
  const [f, setF] = useState({ name: "", email: "", mobile: "", role: "", team_code: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (target) {
      setF({
        name: target.name || "",
        email: target.email || "",
        mobile: target.mobile || "",
        role: target.role || "",
        team_code: target.team_code || "",
      });
    }
  }, [target]);

  const save = async () => {
    const payload = { name: f.name, email: f.email, mobile: f.mobile, role: f.role };
    if (target.team_code) payload.team_code = f.team_code;
    setSaving(true);
    try {
      await api.put(`/users/${target.id}`, payload);
      toast.success("User updated — team member kept in sync");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="edit-user-dialog">
        <DialogHeader>
          <DialogTitle>Edit user</DialogTitle>
          <DialogDescription>
            Changes flow straight into the linked Team Member record. All existing jobs, timers and logs stay attached.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Name</Label>
            <Input className="mt-1.5" value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="edit-user-name" />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" className="mt-1.5" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="edit-user-email" />
          </div>
          <div>
            <Label>Mobile</Label>
            <Input className="mt-1.5" value={f.mobile} onChange={(e) => set("mobile", e.target.value)} data-testid="edit-user-mobile" />
          </div>
          <div>
            <Label>Role</Label>
            <Select value={f.role} onValueChange={(v) => set("role", v)}>
              <SelectTrigger className="mt-1.5" data-testid="edit-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>{roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label>Employee ID</Label>
            <Input
              className="mt-1.5 font-mono"
              value={f.team_code}
              disabled={!target?.team_code}
              onChange={(e) => set("team_code", e.target.value)}
              data-testid="edit-user-team-code"
            />
            <p className="text-xs text-slate-400 mt-1">
              {target?.team_code
                ? "Must be unique. Renaming keeps all history — every record points to the same person."
                : "Managers and Management Users are not team members, so they have no Employee ID."}
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="edit-user-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="edit-user-save">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
